import { useTranslation } from '@/hooks/useTranslation';
import type { ToolDashboardEntry } from '../../utils/dashboard';

type GroupBy = 'machine' | 'operator' | 'material';

type Props = {
  entries: ToolDashboardEntry[];
  groupBy: GroupBy;
  noteKey: string;
};

function getField(entry: ToolDashboardEntry, groupBy: GroupBy): string | null {
  return entry[groupBy];
}

type GroupRow = {
  label: string;
  avg: number;
  count: number;
};

function buildGroups(entries: ToolDashboardEntry[], groupBy: GroupBy): GroupRow[] {
  const map = new Map<string, { total: number; count: number }>();
  for (const entry of entries) {
    const key = getField(entry, groupBy);
    if (!key) continue;
    const current = map.get(key) ?? { total: 0, count: 0 };
    map.set(key, { total: current.total + entry.totalPieces, count: current.count + 1 });
  }
  return Array.from(map.entries())
    .map(([label, { total, count }]) => ({ label, avg: total / count, count }))
    .sort((a, b) => b.avg - a.avg);
}

export function GroupedBarsPanel({ entries, groupBy, noteKey }: Props) {
  const { t } = useTranslation();
  const groups = buildGroups(entries, groupBy);
  const max = groups[0]?.avg ?? 1;

  const topLabel = groups[0]?.label;
  const bottomLabel = groups[groups.length - 1]?.label;
  const ratio =
    groups.length > 1 && groups[groups.length - 1]?.avg > 0
      ? (groups[0].avg / groups[groups.length - 1].avg).toFixed(1)
      : null;

  const insight =
    ratio && topLabel && bottomLabel
      ? t(noteKey)
          .replace('{{top}}', topLabel)
          .replace('{{bottom}}', bottomLabel)
          .replace('{{ratio}}', ratio)
      : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        {groups.length > 0 && (
          // Column captions: what the label, the bar value and the right-hand count stand for.
          <div className="eyebrow flex items-center gap-2">
            <span className="w-24 shrink-0 truncate">
              {t(`analytics.dashboard.grouped.columns.${groupBy}`)}
            </span>
            <span className="flex-1">{t('analytics.dashboard.grouped.columns.avgPieces')}</span>
            <span className="w-14 shrink-0 text-right">
              {t('analytics.dashboard.grouped.columns.tools')}
            </span>
          </div>
        )}
        {groups.map((row) => {
          const pct = max > 0 ? (row.avg / max) * 100 : 0;
          const labelInside = pct > 35;
          return (
            <div key={row.label} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-xs text-foreground truncate">{row.label}</span>
              <div className="relative flex-1 h-5 bg-muted rounded">
                <div
                  className="h-full rounded bg-primary transition-all"
                  style={{ width: `${pct}%` }}
                />
                {labelInside ? (
                  <span className="absolute inset-0 flex items-center pl-2 text-xs font-medium text-primary-foreground">
                    {Math.round(row.avg)}
                  </span>
                ) : (
                  <span className="absolute left-full pl-2 top-0 flex items-center h-full text-xs text-muted-foreground">
                    {Math.round(row.avg)}
                  </span>
                )}
              </div>
              <span className="w-14 shrink-0 text-xs text-muted-foreground text-right">
                {row.count}
              </span>
            </div>
          );
        })}
      </div>
      {insight && (
        <p className="text-xs text-muted-foreground italic mt-1">{insight}</p>
      )}
    </div>
  );
}
